import styled from 'styled-components'
import { tokens } from 'styling/tokens'

export const PageHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 1rem;
`

export const HeaderText = styled.div`
  flex: 1;
  min-width: 0;
`

export const PageTitle = styled.h1`
  margin: 0;
  font-size: 1.5rem;
  font-weight: 500;
  color: ${tokens.text.primary};
`

export const PageSubtitle = styled.div`
  font-size: ${tokens.font.label};
  color: ${tokens.text.muted};
  margin-top: 0.25rem;
`

export const FilterPill = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  height: 2.5rem;
  padding: 0 0.75rem;
  border-radius: ${tokens.radius.control};
  background: ${tokens.surface.glass};
  border: 1px solid ${tokens.border.hairline};
  color: ${tokens.text.secondary};
  font-size: ${tokens.font.body};
  cursor: pointer;
`

export const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  align-items: start;
  gap: 0.75rem;

  @media (max-width: 1100px) {
    grid-template-columns: repeat(2, 1fr);
  }
  @media (max-width: 720px) {
    grid-template-columns: 1fr;
  }
`

export const WidgetBody = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overscroll-behavior: contain;
  scrollbar-width: thin;
  scrollbar-color: transparent transparent;
  transition: scrollbar-color 0.2s ease;

  &:hover {
    scrollbar-color: rgba(255, 255, 255, 0.24) transparent;
  }
  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
    margin: 0.25rem 0;
  }
  &::-webkit-scrollbar-thumb {
    background: transparent;
    border-radius: 999px;
    transition: background 0.2s ease;
  }
  &:hover::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.24);
  }
  &::-webkit-scrollbar-thumb:hover {
    background: rgba(255, 255, 255, 0.36);
  }
`

export const GridArea = styled.div<{ $editing?: boolean }>`
  .react-grid-item > div {
    height: 100%;
    min-height: 0;
  }

  .react-grid-item.react-grid-placeholder {
    background: ${tokens.accent.soft};
    border: 1px dashed ${tokens.accent.base};
    border-radius: ${tokens.radius.card};
    opacity: 0.7;
  }
  .react-grid-item.react-draggable-dragging,
  .react-grid-item.resizing {
    z-index: 5;
    cursor: grabbing;
  }
  .react-resizable-handle {
    opacity: ${(props) => (props.$editing ? '0.6' : '0')};
    transition: opacity 0.15s ease;
  }
  .react-resizable-handle::after {
    border-color: ${tokens.text.secondary};
  }
  ${(props) => props.$editing && '.react-grid-item { cursor: grab; }'}
`

export const AttendanceBody = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`

export const Donut = styled.div<{ $gradient: string }>`
  width: 5.75rem;
  height: 5.75rem;
  flex: none;
  border-radius: 50%;
  background: ${(props) => props.$gradient};
  display: flex;
  align-items: center;
  justify-content: center;
`

export const DonutHole = styled.div`
  width: 4.1rem;
  height: 4.1rem;
  border-radius: 50%;
  background: ${tokens.surface.base};
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`

export const DonutCount = styled.span`
  font-size: 1.25rem;
  font-weight: 500;
  line-height: 1.1;
  color: ${tokens.status.success.fg};
  font-variant-numeric: tabular-nums;
`

export const DonutSub = styled.span`
  font-size: 9px;
  line-height: 1;
  margin-top: 2px;
  color: ${tokens.text.muted};
  letter-spacing: 0.06em;
`

export const Legend = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
`

export const LegendRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`

export const LegendDot = styled.span<{ $color: string }>`
  width: 0.5rem;
  height: 0.5rem;
  border-radius: 2px;
  flex: none;
  background: ${(props) => props.$color};
`

export const LegendLabel = styled.span`
  flex: 1;
  font-size: ${tokens.font.label};
  color: ${tokens.text.secondary};
`

export const LegendValue = styled.span`
  font-size: ${tokens.font.body};
  font-weight: 500;
  color: ${tokens.text.primary};
  font-variant-numeric: tabular-nums;
`

export const LegendPct = styled.span`
  width: 2.25rem;
  text-align: right;
  font-size: ${tokens.font.label};
  color: ${tokens.text.muted};
  font-variant-numeric: tabular-nums;
`

export const BarRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.4rem;
`

export const BarLabel = styled.span`
  width: 6rem;
  font-size: ${tokens.font.label};
  color: ${tokens.text.secondary};
`

export const BarTrack = styled.div`
  flex: 1;
  width: 100%;
  height: 0.45rem;
  min-height: 0.45rem;
  max-height: 0.45rem;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.07);
  overflow: hidden;
`

export const BarFill = styled.div<{ $pct: number; $color?: string }>`
  height: 100%;
  width: ${(props) => props.$pct}%;
  border-radius: 999px;
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.22), rgba(255, 255, 255, 0) 65%),
    ${(props) => props.$color ?? tokens.accent.base};
  transition: width 0.5s cubic-bezier(0.4, 0, 0.2, 1);
`

export const BarValue = styled.span`
  width: 2.25rem;
  text-align: right;
  font-size: ${tokens.font.label};
  font-weight: 500;
  color: ${tokens.text.primary};
  font-variant-numeric: tabular-nums;
`

export const OccupancyValue = styled.div`
  font-size: 1.6rem;
  font-weight: 500;
  color: ${tokens.text.primary};
  font-variant-numeric: tabular-nums;
`

export const OccupancyCaption = styled.div`
  font-size: ${tokens.font.label};
  color: ${tokens.text.muted};
  margin-bottom: 0.75rem;
`

export const ListRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.375rem 0;
  border-bottom: 1px solid ${tokens.border.hairline};

  &:last-child {
    border-bottom: none;
  }
`

export const ListText = styled.div`
  flex: 1;
  min-width: 0;
`

export const ListTitle = styled.div`
  font-size: ${tokens.font.body};
  color: ${tokens.text.primary};
`

export const ListMeta = styled.div`
  font-size: ${tokens.font.micro};
  color: ${tokens.text.muted};
`

export const ShiftRow = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 0.75rem;
`

export const ShiftMetric = styled.div`
  font-size: 1.6rem;
  font-weight: 500;
  color: ${tokens.text.primary};
  font-variant-numeric: tabular-nums;
`

export const ShiftCaption = styled.div`
  font-size: ${tokens.font.label};
  color: ${tokens.text.muted};
`

export const WidgetRemove = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.5rem;
  height: 1.5rem;
  flex: none;
  border: 1px solid ${tokens.border.hairline};
  background: ${tokens.surface.inset};
  border-radius: 6px;
  color: ${tokens.text.secondary};
  cursor: pointer;
  transition: color 0.15s ease, border-color 0.15s ease;

  &:hover {
    color: ${tokens.status.danger.fg};
    border-color: ${tokens.status.danger.fg};
  }
`

export const PaletteOverlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 10000;
  background: rgba(0, 0, 0, 0.45);
  backdrop-filter: blur(2px);
  display: flex;
  justify-content: flex-end;
`

export const PaletteDrawer = styled.div`
  width: 21rem;
  max-width: 92vw;
  height: 100%;
  background: ${tokens.surface.raised};
  border-left: 1px solid ${tokens.border.hairline};
  padding: 1.25rem;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  overflow-y: auto;
`

export const PaletteHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.25rem;
`

export const PaletteTitle = styled.div`
  font-size: ${tokens.font.h2};
  font-weight: 500;
  color: ${tokens.text.primary};
`

export const PaletteClose = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2rem;
  height: 2rem;
  border: 1px solid ${tokens.border.hairline};
  background: transparent;
  border-radius: ${tokens.radius.control};
  color: ${tokens.text.secondary};
  cursor: pointer;

  &:hover {
    color: ${tokens.text.primary};
    background: ${tokens.surface.glass};
  }
`

export const PaletteItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem;
  border: 1px solid ${tokens.border.hairline};
  border-radius: ${tokens.radius.control};
  background: ${tokens.surface.glass};
`

export const PaletteItemInfo = styled.div`
  flex: 1;
  min-width: 0;
`

export const PaletteItemTitle = styled.div`
  font-size: ${tokens.font.body};
  font-weight: 500;
  color: ${tokens.text.primary};
`

export const PaletteItemDesc = styled.div`
  font-size: ${tokens.font.micro};
  color: ${tokens.text.muted};
  margin-top: 0.125rem;
`

export const PaletteAdd = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  flex: none;
  height: 2rem;
  padding: 0 0.75rem;
  border-radius: ${tokens.radius.control};
  border: 1px solid ${tokens.accent.base};
  background: ${tokens.accent.soft};
  color: ${tokens.accent.onDark};
  font-size: ${tokens.font.label};
  font-weight: 500;
  cursor: pointer;
`

export const PaletteEmpty = styled.div`
  font-size: ${tokens.font.label};
  color: ${tokens.text.muted};
  text-align: center;
  padding: 1.5rem 0;
`
